// src/lib/opportunity/oppTodoChecklistUrgency.ts

import { addCalendarDays } from "./oppTodoDueDate.js";

export const OPP_TODO_CHECKLIST_ITEM_PREFIX = "opp_todo:";
export const OPP_TODO_CHECKLIST_APPROACHING_DAYS_DEFAULT = 3;

export type OppTodoChecklistUrgency = "past_due" | "approaching" | "undated";

export function oppTodoChecklistItemId(todoItemId: string): string {
  return `${OPP_TODO_CHECKLIST_ITEM_PREFIX}${todoItemId}`;
}

export function parseOppTodoChecklistItemId(itemId: string): string | null {
  if (!itemId.startsWith(OPP_TODO_CHECKLIST_ITEM_PREFIX)) return null;
  const raw = itemId.slice(OPP_TODO_CHECKLIST_ITEM_PREFIX.length);
  return raw || null;
}

export function shouldSurfaceOppTodoOnChecklist(args: {
  dueDate: string | null;
  todayYmd: string;
  approachingDays?: number;
}): boolean {
  const { dueDate, todayYmd } = args;
  const approachingDays = args.approachingDays ?? OPP_TODO_CHECKLIST_APPROACHING_DAYS_DEFAULT;
  if (!dueDate) return true;
  if (dueDate < todayYmd) return true;
  return dueDate <= addCalendarDays(todayYmd, approachingDays);
}

export function classifyOppTodoChecklistUrgency(args: {
  dueDate: string | null;
  todayYmd: string;
  approachingDays?: number;
}): OppTodoChecklistUrgency {
  const { dueDate, todayYmd } = args;
  if (!dueDate) return "undated";
  if (dueDate < todayYmd) return "past_due";
  return "approaching";
}

export function formatOppTodoChecklistLabel(customerLabel: string, todoLabel: string): string {
  const customer = customerLabel.trim() || "Opportunity";
  const todo = todoLabel.trim() || "Todo";
  return `${customer} — ${todo}`;
}

export function opportunityChecklistCustomerLabel(opp: {
  customer_company?: string | null;
  customer_name?: string | null;
  project_name?: string | null;
}): string {
  const company = typeof opp.customer_company === "string" ? opp.customer_company.trim() : "";
  if (company) return company;
  const name = typeof opp.customer_name === "string" ? opp.customer_name.trim() : "";
  if (name) return name;
  const project = typeof opp.project_name === "string" ? opp.project_name.trim() : "";
  if (project) return project;
  return "Opportunity";
}

export function sortOppTodoChecklistRows<T extends { urgency: OppTodoChecklistUrgency; due_date: string | null; sort_order: number }>(
  rows: readonly T[],
): T[] {
  const rank: Record<OppTodoChecklistUrgency, number> = {
    past_due: 0,
    approaching: 1,
    undated: 2,
  };
  return [...rows].sort((a, b) => {
    const byUrgency = rank[a.urgency] - rank[b.urgency];
    if (byUrgency !== 0) return byUrgency;
    if (a.due_date && b.due_date && a.due_date !== b.due_date) {
      return a.due_date.localeCompare(b.due_date);
    }
    return a.sort_order - b.sort_order;
  });
}

export function oppTodoUrgencyRowClass(urgency: OppTodoChecklistUrgency): string {
  if (urgency === "past_due") {
    return "border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950/35";
  }
  return "border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30";
}
